import umlCanvasEngine from '../UmlCanvasEngine';
import { addSequenceLayoutToCanvas } from './CsSeqCanvasLayoutBuilder';

/**
 * A package/actor participant with at least one seq-participant child, or a
 * standalone seq-participant, belongs to a PRIOR sequence-diagram generation
 * — 'package'/'seq-participant' kinds are shared with class diagrams, so
 * this is how a seq-dia-owned instance is told apart from an unrelated one a
 * user built by hand.
 * @param {object} shape
 * @returns {boolean}
 */
function isSeqPackage(shape) {
  return shape.kind === 'package' && Array.isArray(shape.children) && shape.children.some(function hasSeqChild(c) { return c.kind === 'seq-participant'; });
}

/**
 * Regenerating a sequence diagram for the SAME entry point used to just pile
 * a brand-new set of shapes on top of whatever Generate had already added —
 * every participant/package/lifeline/message/activation got a fresh uuid()
 * every click, so re-generating produced visible duplicates instead of an
 * updated diagram (chat 2026-07-19).
 *
 * Merge strategy (v1, package+participant level ONLY): a NEW namespace
 * package is matched by NAME against an existing seq-dia package already on
 * canvas; a NEW participant is only matched by NAME against participants
 * INSIDE that SAME matched package (a participant's namespace essentially
 * never changes independently of its package, so this avoids having to
 * re-parent a kept participant out of a package that's about to be
 * removed). A match keeps the OLD shape — same id, same dragged position —
 * and just updates the fields that can realistically change (package
 * w/h, participant namespace); a name with no match is genuinely new and
 * gets attached fresh. An old package/participant whose name no longer
 * appears this round is stale (removed/renamed code) and gets deleted.
 * Standalone actor participants (no package) are matched the same way, by
 * name, directly against the top level.
 *
 * Lifelines/messages/activations/bottom-twin participants are NOT identity-
 * matched — every one from the previous generation is removed and the fresh
 * set from `layout` added, with every participant-id reference rewritten
 * through the package/participant matches above. Their geometry is fully
 * recomputed at generation time regardless of merge strategy, so there's
 * nothing meaningful to preserve there (a manually dragged message/
 * activation position IS lost on regenerate — known v1 limitation, same
 * "fix later if it comes up" acceptance as other CsSeqDia gaps).
 * @param {{packages: object[], actors: object[], lifelines: object[], messages: object[], activations: object[], bottomParticipants: object[]}} layout
 */
export function mergeSequenceLayoutIntoCanvas(layout) {
  const existingSeqPackages = umlCanvasEngine.shapes.filter(isSeqPackage);
  const existingSeqPackagesByName = new Map(existingSeqPackages.map(function toEntry(p) { return [p.name, p]; }));
  const existingActors = umlCanvasEngine.shapes.filter(function isTopActor(s) {
    return s.kind === 'seq-participant' && s.isActor && !s.linkedParticipantId;
  });
  const existingActorsByName = new Map(existingActors.map(function toEntry(a) { return [a.name, a]; }));

  const idRewrites = new Map(); // this generation's fresh id -> id to actually use (itself, or a reused old id)
  const keptPackageIds = new Set(); // OLD package ids matched this round
  const keptParticipantIds = new Set(); // OLD participant ids matched this round

  layout.packages.forEach(function resolvePackage(pkg) {
    const oldPkg = existingSeqPackagesByName.get(pkg.name);
    if (!oldPkg) {
      idRewrites.set(pkg.id, pkg.id); // genuinely new namespace box, added as-is below
      return;
    }
    idRewrites.set(pkg.id, oldPkg.id);
    keptPackageIds.add(oldPkg.id);
    oldPkg.w = pkg.w;
    oldPkg.h = pkg.h;

    const oldParticipantsByName = new Map(
      oldPkg.children
        .filter(function isSeqParticipant(c) { return c.kind === 'seq-participant'; })
        .map(function toEntry(c) { return [c.name, c]; })
    );

    pkg.children.forEach(function resolveParticipant(participant) {
      const oldParticipant = oldParticipantsByName.get(participant.name);
      if (!oldParticipant) {
        // Genuinely new class inside an already-existing namespace box —
        // attach directly onto the KEPT package, same convention as
        // UmlCompositeShape.addChild.
        idRewrites.set(participant.id, participant.id);
        participant.parentId = oldPkg.id;
        participant.zIndex = oldPkg.zIndex + 1;
        oldPkg.children.push(participant);
        return;
      }
      idRewrites.set(participant.id, oldParticipant.id);
      keptParticipantIds.add(oldParticipant.id);
      oldParticipant.namespace = participant.namespace;
    });
  });

  layout.actors.forEach(function resolveActor(actor) {
    const oldActor = existingActorsByName.get(actor.name);
    if (!oldActor) {
      idRewrites.set(actor.id, actor.id);
      return;
    }
    idRewrites.set(actor.id, oldActor.id);
    keptParticipantIds.add(oldActor.id);
  });

  // Remove every top-level shape from the PREVIOUS generation that isn't
  // being kept this round (stale packages/actors), plus ALWAYS the derived
  // shapes (lifelines/messages/activations/bottom twins — see doc above).
  umlCanvasEngine.shapes.slice().forEach(function removeStaleOrDerivedShape(shape) {
    const isStalePackage = isSeqPackage(shape) && !keptPackageIds.has(shape.id);
    const isStaleActor = shape.kind === 'seq-participant' && shape.isActor && !shape.linkedParticipantId && !keptParticipantIds.has(shape.id);
    const isBottomTwin = shape.kind === 'seq-participant' && Boolean(shape.linkedParticipantId);
    const isDerived = shape.kind === 'seq-lifeline' || shape.kind === 'seq-message' || shape.kind === 'seq-activation';
    if (isStalePackage || isStaleActor || isBottomTwin || isDerived) {
      umlCanvasEngine.removeShape(shape.id);
    }
  });

  // A KEPT package's own stale participant (its name no longer appears this
  // round — renamed/removed class) still needs removing — the sweep above
  // never reaches it since its parent package stays.
  existingSeqPackages.forEach(function pruneStaleParticipants(oldPkg) {
    if (!keptPackageIds.has(oldPkg.id)) return; // already fully removed above
    oldPkg.children.slice().forEach(function removeIfNoLongerPresent(child) {
      if (child.kind === 'seq-participant' && !keptParticipantIds.has(child.id)) {
        umlCanvasEngine.removeShape(child.id);
      }
    });
  });

  // What's left to actually add: genuinely-new packages/actors only (kept
  // ones already got their new children attached in place above), plus the
  // always-rebuilt lifelines/messages/activations/bottom-twins, with every
  // participant-id reference rewritten through idRewrites first.
  const filteredLayout = {
    packages: layout.packages.filter(function keepUnmatchedPackage(pkg) { return !keptPackageIds.has(idRewrites.get(pkg.id)); }),
    actors: layout.actors.filter(function keepUnmatchedActor(a) { return !keptParticipantIds.has(idRewrites.get(a.id)); }),
    lifelines: layout.lifelines.map(function rewireLifeline(l) {
      l.participantId = idRewrites.get(l.participantId) ?? l.participantId;
      return l;
    }),
    activations: layout.activations.map(function rewireActivation(a) {
      a.participantId = idRewrites.get(a.participantId) ?? a.participantId;
      return a;
    }),
    messages: layout.messages.map(function rewireMessage(m) {
      m.fromParticipantId = idRewrites.get(m.fromParticipantId) ?? m.fromParticipantId;
      m.toParticipantId = idRewrites.get(m.toParticipantId) ?? m.toParticipantId;
      return m;
    }),
    bottomParticipants: layout.bottomParticipants.map(function rewireBottomTwin(bp) {
      bp.linkedParticipantId = idRewrites.get(bp.linkedParticipantId) ?? bp.linkedParticipantId;
      return bp;
    }),
  };

  addSequenceLayoutToCanvas(filteredLayout);
  umlCanvasEngine.notify(); // oldPkg.w/h + oldParticipant.namespace + children.push mutations above bypassed the engine's own setters
}
